import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Checkbox,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axiosInstance from '../utils/axios';
import { UserRequest, RequestData } from '../types/types';
import { useAuth } from '../context/AuthContext';
import { getCurrentDateKZ, formatDateForDisplay } from '../utils/dateUtils';
import { t } from '../utils/translations';

// Product card component
const ProductCard = ({ 
  product, 
  isSelected, 
  quantity, 
  rememberedQuantities,
  onSelect, 
  onQuantityChange,
  isFranchise 
}: { 
  product: { product_id: number; product_name: string; wasInRequest: boolean; price?: string };
  isSelected: boolean;
  quantity: number;
  rememberedQuantities: { [key: number]: number };
  onSelect: (selected: boolean) => void;
  onQuantityChange: (quantity: number) => void;
  isFranchise: boolean;
}) => {
  return (
    <Paper 
      elevation={isSelected ? 3 : 1}
      sx={{ 
        p: 2, 
        borderRadius: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? '#ec248f' : 'divider',
        transition: 'all 0.2s ease',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          cursor: 'pointer'
        }
      }}
      onClick={() => onSelect(!isSelected)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox 
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation(); // Prevent the Paper's onClick from firing
            onSelect(e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()} // Prevent the Paper's onClick from firing
          sx={{ 
            color: '#ec248f',
            '&.Mui-checked': {
              color: '#ec248f',
            },
          }}
        />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
            {product.product_name}
          </Typography>
          {product.wasInRequest && (
            <Typography variant="caption" color="text.secondary">
              {t('Previously requested')}
            </Typography>
          )}
          {isFranchise && product.price && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                color: '#ec248f', 
                fontWeight: 'medium',
                mt: 0.5 
              }}
            >
              {product.price}
            </Typography>
          )}
        </Box>
      </Box>
      
      {isSelected && (
        <Box 
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()} // Prevent quantity controls from triggering selection
        >
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onQuantityChange(Math.max(1, quantity - 1));
            }}
            disabled={quantity <= 1}
            sx={{ color: '#ec248f' }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          
          <TextField
            size="small"
            type="number"
            value={quantity}
            onChange={(e) => {
              const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
              onQuantityChange(newQuantity);
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{
              min: 1,
              style: { textAlign: 'center' }
            }}
            sx={{ 
              width: 50,
              mx: 1,
              '& .MuiOutlinedInput-root': {
                height: 36,
                borderRadius: '18px',
                backgroundColor: 'rgba(236, 36, 143, 0.1)',
                '& fieldset': {
                  borderColor: 'rgba(236, 36, 143, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#ec248f',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ec248f',
                },
              },
              '& .MuiOutlinedInput-input': {
              color: '#ec248f',
                fontWeight: 'bold',
                padding: '8px 12px',
                /* Hide number input spinner buttons */
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
                /* Firefox */
                '&[type=number]': {
                  '-moz-appearance': 'textfield',
                },
              }
            }}
          />
          
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onQuantityChange(quantity + 1);
            }}
            sx={{ color: '#ec248f' }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

interface GroupedItems {
  [category: string]: {
    categoryName: string;
    items: {
      id: number;
      product_id: number;
      product_name: string;
      quantity: number;
      wasInRequest: boolean;
      price?: string;
    }[];
  };
}

interface Product {
  id: number;
  name: string;
  barcode: number;
  created_at: string;
  price?: string;
}

interface Category {
  id: number;
  name: string;
  products: Product[];
  products_count: number;
}

interface ProductsResponse {
  categories: Category[];
}

interface EditRequestProps {
  isCreating?: boolean;
  pointOfSaleId?: number;
  duplicateRequest?: UserRequest;
}

const EditRequest = ({ isCreating = false, pointOfSaleId, duplicateRequest }: EditRequestProps) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { pointOfSale, user } = useAuth();
  const [request, setRequest] = useState<UserRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<{ [key: number]: { product_id: number; quantity: number } }>({});
  const [activeStep, setActiveStep] = useState(0);
  const [groupedItems, setGroupedItems] = useState<GroupedItems>({});
  const [rememberedQuantities, setRememberedQuantities] = useState<{ [key: number]: number }>({});
  const [shippingDate, setShippingDate] = useState(getCurrentDateKZ());
  const [typeOfDelivery, setTypeOfDelivery] = useState('delivery');
  const [shippingAddress, setShippingAddress] = useState('');
  const [documentType, setDocumentType] = useState('invoice');
  
  // Use pointOfSaleId from props for creation, or from context for editing
  const currentPointOfSaleId = isCreating ? pointOfSaleId : pointOfSale?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentPointOfSaleId) {
          navigate('/requests');
          return;
        }

        if (isCreating) {
          // Only fetch products for creation
          const productsResponse = await axiosInstance.get<ProductsResponse>(
            `/api/v1/products/?point_of_sale_id=${currentPointOfSaleId}`
          );
          
          // Initialize selected products from duplicate request if available
          let initialSelected: { [key: number]: { product_id: number; quantity: number } } = {};
          if (duplicateRequest && duplicateRequest.items) {
            duplicateRequest.items.forEach(item => {
              initialSelected[item.product_id] = {
                product_id: item.product_id,
                quantity: item.quantity
              };
            });
            setSelectedProducts(initialSelected);
            
            // Set comment and other fields from duplicate request
            setComment(duplicateRequest.comment || '');
            if (user?.type === 'franchise' && duplicateRequest.shipping_date) {
              setShippingDate(duplicateRequest.shipping_date);
            }
            if (user?.type === 'horeca') {
              setTypeOfDelivery(duplicateRequest.type_of_delivery || 'delivery');
              setDocumentType(duplicateRequest.document_type || 'invoice');
              setShippingAddress(duplicateRequest.shipping_address || '');
            }
          }
          
          // Initialize grouped items
          const grouped = productsResponse.data.categories.reduce<GroupedItems>((acc, category) => {
            acc[category.name] = {
              categoryName: category.name,
              items: category.products.map(product => ({
                id: product.id,
                product_id: product.id,
                product_name: product.name,
                quantity: initialSelected[product.id]?.quantity || 1,
                wasInRequest: !!initialSelected[product.id],
                price: product.price
              }))
            };
            return acc;
          }, {});

          setGroupedItems(grouped);
        } else {
          // Fetch both request and products for editing
          const [requestResponse, productsResponse] = await Promise.all([
            axiosInstance.get<UserRequest>(`/api/v1/user/requests/${id}/`),
            axiosInstance.get<ProductsResponse>(`/api/v1/products/?point_of_sale_id=${currentPointOfSaleId}`)
          ]);

          // Allow editing for all statuses except 'printed'
          if (requestResponse.data.status === 'printed') {
            navigate('/requests');
            return;
          }

          setRequest(requestResponse.data);
          setComment(requestResponse.data.comment || '');
          if (user?.type === 'franchise' && requestResponse.data.shipping_date) {
            // Only override the default date if there's an existing shipping date in the request
            setShippingDate(requestResponse.data.shipping_date);
          }
          if (user?.type === 'horeca') {
            // Use the existing value from the request or keep the default 'delivery' value
            setTypeOfDelivery(requestResponse.data.type_of_delivery || 'delivery');
            // Set document type if it exists in the request
            setDocumentType(requestResponse.data.document_type || 'invoice');
          }
          // Set shipping address if it exists
          setShippingAddress(requestResponse.data.shipping_address || '');

          // Initialize selected products from request items
          const initialSelected: { [key: number]: { product_id: number; quantity: number } } = {};
          requestResponse.data.items.forEach(item => {
            initialSelected[item.product_id] = {
              product_id: item.product_id,
              quantity: item.quantity
            };
          });
          setSelectedProducts(initialSelected);

          // Group products and mark those that were in request
          const grouped = productsResponse.data.categories.reduce<GroupedItems>((acc, category) => {
            acc[category.name] = {
              categoryName: category.name,
              items: category.products.map(product => ({
                id: product.id,
                product_id: product.id,
                product_name: product.name,
                quantity: initialSelected[product.id]?.quantity || 1,
                wasInRequest: !!initialSelected[product.id],
                price: product.price
              }))
            };
            return acc;
          }, {});

          setGroupedItems(grouped);
        }
      } catch (error) {
        console.error('Error in EditRequest:', error);
        navigate('/requests');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, isCreating, currentPointOfSaleId, user?.type, duplicateRequest]);

  // Clear shipping address when delivery type changes to pickup
  useEffect(() => {
    if (user?.type === 'horeca' && typeOfDelivery === 'pickup') {
      setShippingAddress('');
    }
  }, [typeOfDelivery, user?.type]);

  const handleQuantityChange = (productId: number, quantity: number) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: { ...prev[productId], quantity }
    }));
    
    // Remember this quantity for future use
    setRememberedQuantities(prev => ({
      ...prev,
      [productId]: quantity
    }));
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts(prev => {
      const newSelected = { ...prev };
      delete newSelected[productId];
      return newSelected;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check minimum order requirements before submission
    if (!checkMinimumOrderRequirement()) {
      alert(t('For horeca, minimum order quantity for Узкие Кюветы is 3 items'));
      return;
    }
    
    try {
      const requestData: RequestData = {
        point_of_sale_id: currentPointOfSaleId!,
        comment,
        items: Object.values(selectedProducts)
      };

      // Add data based on user type
      if (user?.type === 'franchise' && shippingDate) {
        requestData.shipping_date = shippingDate;
      }

      // Add type of delivery for horeca users
      if (user?.type === 'horeca') {
        requestData.type_of_delivery = typeOfDelivery;
        requestData.document_type = documentType;
        
        // Add shipping address if delivery type is not pickup
        if (typeOfDelivery !== 'pickup' && shippingAddress) {
          requestData.shipping_address = shippingAddress;
        }
      }

      if (isCreating) {
        await axiosInstance.post('/api/v1/requests/', requestData);
      } else {
        await axiosInstance.put(`/api/v1/user/requests/${id}/`, requestData);
      }
      navigate('/requests');
    } catch (error) {
      console.error('Error saving request:', error);
    }
  };

  // Function to check if the minimum order requirement is met for horeca users
  const checkMinimumOrderRequirement = () => {
    // Only apply this restriction to horeca users
    if (user?.type !== 'horeca') return true;
    
    const categories = Object.values(groupedItems);
    const kuvetsCategory = categories.find(category => category.categoryName === 'Узкие Кюветы');
    
    // If category doesn't exist or user didn't select any products from it, validation passes
    if (!kuvetsCategory) return true;
    
    // Calculate total quantity for selected products in this category
    let totalQuantity = 0;
    kuvetsCategory.items.forEach(item => {
      if (selectedProducts[item.product_id]) {
        totalQuantity += selectedProducts[item.product_id].quantity;
      }
    });
    
    // Check if any products are selected from this category and if minimum requirement is met
    const hasSelectedProducts = totalQuantity > 0;
    return !hasSelectedProducts || totalQuantity >= 3;
  };
  
  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent default button behavior
    e.preventDefault();
    
    // Check if current step is a category with the name "Узкие Кюветы"
    const categories = Object.values(groupedItems);
    const isKuvetsStep = activeStep < categories.length && 
                         categories[activeStep]?.categoryName === 'Узкие Кюветы';
    
    // If it's the kuvets step for horeca users, validate minimum order
    if (isKuvetsStep && user?.type === 'horeca') {
      const kuvetsCategory = categories[activeStep];
      let totalQuantity = 0;
      
      kuvetsCategory.items.forEach(item => {
        if (selectedProducts[item.product_id]) {
          totalQuantity += selectedProducts[item.product_id].quantity;
        }
      });
      
      // If products are selected but less than 3 total, show error
      if (totalQuantity > 0 && totalQuantity < 3) {
        alert(t('For horeca, minimum order quantity for Узкие Кюветы is 3 items'));
        return;
      }
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderStepContent = (step: number) => {
    const categories = Object.values(groupedItems);
    const shippingDateStepIndex = categories.length;
    const detailsStepIndex = user?.type === 'franchise' ? categories.length + 1 : categories.length;
    const reviewStepIndex = user?.type === 'franchise' ? categories.length + 2 : categories.length + 1;
    
    // Shipping Date step (only for franchise users)
    if (user?.type === 'franchise' && step === shippingDateStepIndex) {
      return (
        <Card sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('Shipping Date')}
            </Typography>
              <TextField
                fullWidth
                label={t('Shipping Date')}
                type="date"
                value={shippingDate}
                onChange={(e) => setShippingDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: getCurrentDateKZ()
                }}
                sx={{ mb: 3 }}
              />
          </CardContent>
        </Card>
      );
    }
    
    // Add Details step
    if (step === detailsStepIndex) {
      return (
        <Card sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('Add Details')}
            </Typography>

            {/* Show document type field only for horeca users */}
            {user?.type === 'horeca' && (
              <TextField
                fullWidth
                label={t('Document Type')}
                select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                sx={{ mb: 3 }}
              >
                <MenuItem value="invoice">{t('Invoice')}</MenuItem>
                <MenuItem value="waybill">{t('Waybill')}</MenuItem>
              </TextField>
            )}
            
            {/* Show type of delivery field only for horeca users */}
            {user?.type === 'horeca' && (
              <TextField
                fullWidth
                label={t('Type of Delivery')}
                select
                value={typeOfDelivery}
                onChange={(e) => setTypeOfDelivery(e.target.value)}
                sx={{ mb: 3 }}
              >
                <MenuItem value="delivery">{t('Delivery')}</MenuItem>
                <MenuItem value="pickup">{t('Pickup')}</MenuItem>
              </TextField>
            )}
            
            {/* Shipping Address field only for horeca users and when delivery type is not pickup */}
            {user?.type === 'horeca' && typeOfDelivery !== 'pickup' && (
              <TextField
                fullWidth
                label={t('Shipping Address')}
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder={t('Enter delivery address...')}
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            )}
            <TextField
              fullWidth
              label={t('Comment')}
              multiline
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('Add any additional notes or comments here...')}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
          </CardContent>
        </Card>
      );
    }
    
    // Review step
    if (step === reviewStepIndex) {
      return (
        <Card sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('Review Your Request')}
            </Typography>
            
            {/* Show selected items grouped by category */}
            {categories.map(category => {
              const hasSelectedItems = category.items.some(item => 
                selectedProducts[item.product_id]
              );

              if (!hasSelectedItems) return null;

              // Calculate total quantity for this category
              const categoryTotal = category.items
                .filter(item => selectedProducts[item.product_id])
                .reduce((sum, item) => sum + selectedProducts[item.product_id].quantity, 0);

              // Calculate total price for this category (for franchise users)
              const categoryTotalPrice = user?.type === 'franchise' 
                ? category.items
                    .filter(item => selectedProducts[item.product_id] && item.price)
                    .reduce((sum, item) => {
                      // Extract numeric value from price string (e.g., "1000 ₸", "1,000 ₸", "1000" -> 1000)
                      const priceStr = item.price || '';
                      // Remove all non-digit characters except dots and commas, then replace comma with dot
                      const cleanedPrice = priceStr.replace(/[^\d.,]/g, '').replace(',', '.');
                      const numericPrice = parseFloat(cleanedPrice) || 0;
                      const quantity = selectedProducts[item.product_id].quantity;
                      return sum + (numericPrice * quantity);
                    }, 0)
                : 0;

              return (
                <Box key={category.categoryName} sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      bgcolor: 'primary.lighter',
                      color: 'primary.dark',
                      p: 1.5,
                      mb: 2,
                      fontWeight: 'bold',
                      borderRadius: 1
                    }}
                  >
                    {category.categoryName}
                  </Typography>
                  <Table 
                    size="small" 
                    sx={{ 
                      tableLayout: 'fixed',
                      width: '100%',
                      '& .MuiTableCell-root': { py: 1.5 } 
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>{t('Product')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '25%' }}>{t('Quantity')}</TableCell>
                        {user?.type === 'franchise' && (
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>{t('Price')}</TableCell>
                        )}
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>{t('Actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.items
                        .filter(item => selectedProducts[item.product_id])
                        .map(item => (
                          <TableRow key={item.product_id}>
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_name}</TableCell>
                            <TableCell align="center" sx={{ width: '25%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleQuantityChange(item.product_id, Math.max(1, selectedProducts[item.product_id].quantity - 1))}
                                  disabled={selectedProducts[item.product_id].quantity <= 1}
                                  sx={{ color: '#ec248f' }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedProducts[item.product_id].quantity}
                                  onChange={(e) => {
                                    const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                                    handleQuantityChange(item.product_id, newQuantity);
                                  }}
                                  inputProps={{
                                    min: 1,
                                    style: { textAlign: 'center' }
                                  }}
                                  sx={{ 
                                    width: 60,
                                    '& .MuiOutlinedInput-root': {
                                      height: 32,
                                      borderRadius: '16px',
                                      backgroundColor: 'rgba(236, 36, 143, 0.1)',
                                      '& fieldset': {
                                        borderColor: 'rgba(236, 36, 143, 0.3)',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#ec248f',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#ec248f',
                                      },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      color: '#ec248f',
                                      fontWeight: 'bold',
                                      padding: '6px 8px',
                                      fontSize: '0.875rem',
                                      /* Hide number input spinner buttons */
                                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                      },
                                      /* Firefox */
                                      '&[type=number]': {
                                        '-moz-appearance': 'textfield',
                                      },
                                    }
                                  }}
                                />
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleQuantityChange(item.product_id, selectedProducts[item.product_id].quantity + 1)}
                                  sx={{ color: '#ec248f' }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            {user?.type === 'franchise' && (
                              <TableCell align="center" sx={{ width: '20%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.price ? `${item.price}` : '-'}</TableCell>
                            )}
                            <TableCell align="center" sx={{ width: '15%' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveProduct(item.product_id)}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)' 
                                  }
                                }}
                                title={t('Remove Product')}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      }
                      {/* Total row */}
                      <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontStyle: 'italic', width: '40%' }}>Общее количество</TableCell>
                        <TableCell align="center" sx={{ width: '25%' }}>
                              <Chip 
                            label={`${categoryTotal}`}
                                color="primary" 
                                size="small" 
                                variant="filled"
                                sx={{
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  minWidth: '40px',
                                  '& .MuiChip-label': {
                                    display: 'inline-block',
                                    padding: '0 6px'
                                  }
                                }}
                              />
                            </TableCell>
                            {user?.type === 'franchise' && (
                          <TableCell align="center" sx={{ width: '20%' }}>
                            {categoryTotalPrice > 0 ? (
                              <Chip 
                                label={`${categoryTotalPrice.toLocaleString('ru-RU')} ₸`}
                                color="primary" 
                                size="small" 
                                variant="filled"
                                sx={{
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  '& .MuiChip-label': {
                                    display: 'inline-block',
                                    padding: '0 6px',
                                    whiteSpace: 'nowrap'
                                  }
                                }}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                            )}
                        <TableCell sx={{ width: '15%' }}></TableCell>
                          </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              );
            })}

            {/* Show shipping date for franchise users */}
            {user?.type === 'franchise' && (
              <Box sx={{ mt: 4, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {t('Shipping Date')}
                </Typography>
                <Paper sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  {shippingDate ? formatDateForDisplay(shippingDate) : t('No shipping date set')}
                </Paper>
              </Box>
            )}

            {/* Show type of delivery for horeca users */}
            {user?.type === 'horeca' && (
              <Box sx={{ mt: 4, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {t('Type of Delivery')}
                </Typography>
                <Paper sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  {typeOfDelivery ? t(typeOfDelivery.charAt(0).toUpperCase() + typeOfDelivery.slice(1)) : t('No delivery type selected')}
                </Paper>
              </Box>
            )}

            {/* Show document type for horeca users */}
            {user?.type === 'horeca' && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {t('Document Type')}
                </Typography>
                <Paper sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  {documentType ? t(documentType === 'invoice' ? 'Invoice' : 'Waybill') : t('No document type selected')}
                </Paper>
              </Box>
            )}

            {/* Show shipping address only for horeca users when not pickup */}
            {user?.type === 'horeca' && typeOfDelivery !== 'pickup' && shippingAddress && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {t('Shipping Address')}
                </Typography>
                <Paper sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  {shippingAddress || t('No shipping address provided')}
                </Paper>
              </Box>
            )}

            {/* Show comment */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t('Comment')}
              </Typography>
              <Paper sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                {comment || t('No comment added')}
              </Paper>
            </Box>
            
            {/* Submit button at the end of review page */}
            <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                fullWidth
                disabled={Object.keys(selectedProducts).length === 0}
                sx={{ 
                  borderRadius: 2, 
                  py: 1.5,
                  boxShadow: '0 4px 10px rgba(76, 175, 80, 0.25)',
                  background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
                  '&:hover': {
                    boxShadow: '0 6px 12px rgba(76, 175, 80, 0.35)',
                  }
                }}
              >
                {isCreating ? t('Create Request') : t('Update Request')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Category step - only render if step is within categories range
    if (step >= categories.length) {
      return null;
    }

    const category = categories[step];
    
    // Calculate total quantity for current category
    const categoryTotal = category.items
      .filter(item => selectedProducts[item.product_id])
      .reduce((sum, item) => sum + selectedProducts[item.product_id].quantity, 0);
    
    return (
      <Card sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {category.categoryName}
          </Typography>
            {categoryTotal > 0 && (
              <Chip 
                label={`${categoryTotal}`}
                color="primary" 
                size="small" 
                variant="filled"
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  minWidth: '40px',
                  '& .MuiChip-label': {
                    display: 'inline-block',
                    padding: '0 6px'
                  }
                }}
              />
            )}
          </Box>
          
          {/* Show minimum order requirement for horeca users in Узкие Кюветы category */}
          {user?.type === 'horeca' && category.categoryName === 'Узкие Кюветы' && (
            <Box 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: 2, 
                bgcolor: 'rgba(236, 36, 143, 0.08)', 
                border: '1px dashed rgba(236, 36, 143, 0.3)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#ec248f' }}>
                {t('For horeca, minimum order quantity for Узкие Кюветы is 3 items')}
              </Typography>
            </Box>
          )}
          
          <Grid container spacing={2}>
            {category.items.map((item) => {
              const isSelected = !!selectedProducts[item.product_id];
              const quantity = isSelected ? selectedProducts[item.product_id].quantity : 1;

              return (
                <Grid item xs={12} key={item.product_id}>
                  <ProductCard
                    product={item}
                    isSelected={isSelected}
                    quantity={quantity}
                    rememberedQuantities={rememberedQuantities}
                    onSelect={(selected) => {
                      const newSelected = { ...selectedProducts };
                      if (selected) {
                        // Use remembered quantity if available, otherwise use default
                        const rememberedQuantity = rememberedQuantities[item.product_id];
                        const initialQuantity = rememberedQuantity || (item.wasInRequest ? item.quantity : 1);
                        
                        newSelected[item.product_id] = {
                          product_id: item.product_id,
                          quantity: initialQuantity
                        };
                      } else {
                        delete newSelected[item.product_id];
                      }
                      setSelectedProducts(newSelected);
                    }}
                    onQuantityChange={(newQuantity) => {
                      handleQuantityChange(item.product_id, newQuantity);
                    }}
                    isFranchise={user?.type === 'franchise'}
                  />
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) return (
    <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Container>
  );
  
  if (!isCreating && !request) return (
    <Container>
      <Typography variant="h6" color="error">Request not found</Typography>
    </Container>
  );

  const categories = Object.values(groupedItems);
  const steps = [
    ...categories.map(cat => cat.categoryName),
    ...(user?.type === 'franchise' ? [t('Shipping Date')] : []),
    t('Add Details'),
    t('Review & Submit')
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {isCreating ? t('Create New Request') : `${t('Edit Request')} #${request?.id}`}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('Point of Sale')}: <Chip label={pointOfSale?.name} size="small" sx={{ ml: 1 }} />
        </Typography>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
            {t('Step')} {activeStep + 1} {t('of')} {steps.length}: <strong>{t(steps[activeStep])}</strong>
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={(activeStep / (steps.length - 1)) * 100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  }
                }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, minWidth: '40px' }}>
              {Math.round((activeStep / (steps.length - 1)) * 100)}%
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {steps.map((label, index) => (
              <Chip
                key={label}
                label={label}
                size="small"
                variant={index === activeStep ? "filled" : "outlined"}
                color={index === activeStep ? "primary" : "default"}
                onClick={() => {
                  // Allow clicking on all steps, not just completed ones
                  setActiveStep(index);
                }}
                sx={{ 
                  cursor: 'pointer',
                  opacity: index === activeStep ? 1 : 0.7,
                  fontWeight: index === activeStep ? 'bold' : 'normal',
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      <Box component="form" onSubmit={handleSubmit}>
        {/* Display navigation buttons but hide the main action button for the review page */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          mt: 4,
          gap: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-start' }
          }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/requests')}
              sx={{ 
                borderRadius: 2,
                borderColor: 'rgba(236, 36, 143, 0.5)',
                color: '#ec248f',
                '&:hover': {
                  backgroundColor: 'rgba(236, 36, 143, 0.04)',
                  borderColor: '#ec248f'
                }
              }}
            >
              {t('Requests')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<span>←</span>}
              sx={{ borderRadius: 2 }}
            >
              {t('Back')}
            </Button>
          </Box>
          
          {/* Only show the Next button if not on the review page */}
          {activeStep !== (user?.type === 'franchise' ? categories.length + 2 : categories.length + 1) && (
            <Button
              variant="contained"
              onClick={handleNext}
              type="button"
              endIcon={<span>→</span>}
              sx={{ 
                borderRadius: 2,
                px: 4,
                width: { xs: '100%', sm: 'auto' },
                boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
                background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(236, 36, 143, 0.35)',
                }
              }}
            >
              {t('Next')}
            </Button>
          )}
        </Box>

        {renderStepContent(activeStep)}
      </Box>
    </Container>
  );
};

export default EditRequest; 